import React from 'react'
import {
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SecurityIcon from '@mui/icons-material/Security'
import LockIcon from '@mui/icons-material/Lock'
import ShieldIcon from '@mui/icons-material/Shield'

import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import ConfigToggle from '../components/ConfigToggle'
import ConfigSelect from '../components/ConfigSelect'
import { SecurityConfig } from '../types/EnvironmentConfig.types'
import { environmentConfigSectionDividerSx, environmentConfigSectionRootSx } from '../utils/environmentConfigChrome'

interface SecuritySettingsProps {
  config: SecurityConfig
  onChange: (updates: Partial<SecurityConfig>) => void
  disabled?: boolean
}

/**
 * Security Settings Section
 * Manages password policies, session management, and security features
 */
const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  config,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme()
  const handleChange = (field: keyof SecurityConfig, value: any) => {
    onChange({ [field]: value })
  }

  const handleNestedChange = (parentField: keyof SecurityConfig, childField: string, value: any) => {
    const currentValue = config[parentField] as any
    onChange({
      [parentField]: {
        ...currentValue,
        [childField]: value,
      },
    })
  }

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      {/* Password Policy */}
      <ConfigSection
        title="Password Policy"
        description="Configure password requirements and security"
        icon={<LockIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Minimum Length"
              value={config.password.minLength.toString()}
              onChange={(value) => handleNestedChange('password', 'minLength', parseInt(value) || 8)}
              type="number"
              placeholder="8"
              disabled={disabled}
              helpText="Minimum password length required"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Max Age (days)"
              value={config.password.maxAge.toString()}
              onChange={(value) => handleNestedChange('password', 'maxAge', parseInt(value) || 90)}
              type="number"
              placeholder="90"
              disabled={disabled}
              helpText="Number of days before password expires"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Prevent Reuse (count)"
              value={config.password.preventReuse.toString()}
              onChange={(value) => handleNestedChange('password', 'preventReuse', parseInt(value) || 5)}
              type="number"
              placeholder="5"
              disabled={disabled}
              helpText="Number of previous passwords to prevent reuse"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Require Uppercase"
              value={config.password.requireUppercase}
              onChange={(value) => handleNestedChange('password', 'requireUppercase', value)}
              description="Require at least one uppercase letter"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Require Lowercase"
              value={config.password.requireLowercase}
              onChange={(value) => handleNestedChange('password', 'requireLowercase', value)}
              description="Require at least one lowercase letter"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Require Numbers"
              value={config.password.requireNumbers}
              onChange={(value) => handleNestedChange('password', 'requireNumbers', value)}
              description="Require at least one number"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Require Special Characters"
              value={config.password.requireSpecialChars}
              onChange={(value) => handleNestedChange('password', 'requireSpecialChars', value)}
              description="Require at least one special character"
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Session Management */}
      <ConfigSection
        title="Session Management"
        description="Configure session timeouts and security"
        icon={<SecurityIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Session Timeout (minutes)"
              value={config.session.timeout.toString()}
              onChange={(value) => handleNestedChange('session', 'timeout', parseInt(value) || 60)}
              type="number"
              placeholder="60"
              disabled={disabled}
              helpText="Session timeout in minutes of inactivity"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Max Concurrent Sessions"
              value={config.session.maxConcurrent.toString()}
              onChange={(value) => handleNestedChange('session', 'maxConcurrent', parseInt(value) || 3)}
              type="number"
              placeholder="3"
              disabled={disabled}
              helpText="Maximum concurrent sessions per user"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Remember Me"
              value={config.session.rememberMe}
              onChange={(value) => handleNestedChange('session', 'rememberMe', value)}
              description="Allow users to stay logged in"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Secure Only"
              value={config.session.secureOnly}
              onChange={(value) => handleNestedChange('session', 'secureOnly', value)}
              description="Require HTTPS for all sessions"
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Rate Limiting */}
      <ConfigSection
        title="Rate Limiting"
        description="Configure API rate limiting to prevent abuse"
        icon={<ShieldIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Rate Limiting"
              value={config.rateLimiting.enabled}
              onChange={(value) => handleNestedChange('rateLimiting', 'enabled', value)}
              description="Enable API rate limiting to prevent abuse"
              disabled={disabled}
            />
          </Grid>
          
          {config.rateLimiting.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Requests Per Minute"
                  value={config.rateLimiting.requestsPerMinute.toString()}
                  onChange={(value) => handleNestedChange('rateLimiting', 'requestsPerMinute', parseInt(value) || 60)}
                  type="number"
                  placeholder="60"
                  disabled={disabled}
                  helpText="Maximum requests per minute per user"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Requests Per Hour"
                  value={config.rateLimiting.requestsPerHour.toString()}
                  onChange={(value) => handleNestedChange('rateLimiting', 'requestsPerHour', parseInt(value) || 1000)}
                  type="number"
                  placeholder="1000"
                  disabled={disabled}
                  helpText="Maximum requests per hour per user"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Two-Factor Authentication */}
      <ConfigSection
        title="Two-Factor Authentication"
        description="Configure 2FA settings and requirements"
        icon={<SecurityIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Enable 2FA"
              value={config.twoFactor.enabled}
              onChange={(value) => handleNestedChange('twoFactor', 'enabled', value)}
              description="Enable two-factor authentication"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigToggle
              label="Require 2FA"
              value={config.twoFactor.required}
              onChange={(value) => handleNestedChange('twoFactor', 'required', value)}
              description="Require all users to use 2FA"
              disabled={disabled}
            />
          </Grid>
          
          {config.twoFactor.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigSelect
                label="2FA Methods"
                value={config.twoFactor.methods}
                onChange={(value) => handleNestedChange('twoFactor', 'methods', value)}
                options={[
                  { value: ['email'], label: 'Email Only' },
                  { value: ['sms'], label: 'SMS Only' },
                  { value: ['totp'], label: 'TOTP Only' },
                  { value: ['email', 'sms'], label: 'Email & SMS' },
                  { value: ['email', 'totp'], label: 'Email & TOTP' },
                  { value: ['sms', 'totp'], label: 'SMS & TOTP' },
                  { value: ['email', 'sms', 'totp'], label: 'All Methods' },
                ]}
                multiple={true}
                disabled={disabled}
                helpText="Available 2FA methods for users"
              />
            </Grid>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* IP Restrictions */}
      <ConfigSection
        title="IP Restrictions"
        description="Configure IP-based access control"
        icon={<ShieldIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable IP Restrictions"
              value={config.ipRestrictions.enabled}
              onChange={(value) => handleNestedChange('ipRestrictions', 'enabled', value)}
              description="Enable IP-based access control"
              disabled={disabled}
            />
          </Grid>
          
          {config.ipRestrictions.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Whitelist IPs"
                  value={config.ipRestrictions.whitelist.join(', ')}
                  onChange={(value) => handleNestedChange('ipRestrictions', 'whitelist', value.split(',').map(ip => ip.trim()).filter(ip => ip))}
                  type="text"
                  placeholder="192.168.1.1, 10.0.0.0/24"
                  disabled={disabled}
                  helpText="Comma-separated list of allowed IP addresses/ranges"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Blacklist IPs"
                  value={config.ipRestrictions.blacklist.join(', ')}
                  onChange={(value) => handleNestedChange('ipRestrictions', 'blacklist', value.split(',').map(ip => ip.trim()).filter(ip => ip))}
                  type="text"
                  placeholder="203.0.113.1, 198.51.100.0/24"
                  disabled={disabled}
                  helpText="Comma-separated list of blocked IP addresses/ranges"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Encryption */}
      <ConfigSection
        title="Encryption"
        description="Configure encryption algorithms and key rotation"
        icon={<LockIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Encryption Algorithm"
              value={config.encryption.algorithm}
              onChange={(value) => handleNestedChange('encryption', 'algorithm', value)}
              options={[
                { value: 'AES-128', label: 'AES-128' },
                { value: 'AES-256', label: 'AES-256 (Recommended)' },
              ]}
              disabled={disabled}
              helpText="Encryption algorithm for sensitive data"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Key Rotation (days)"
              value={config.encryption.keyRotation.toString()}
              onChange={(value) => handleNestedChange('encryption', 'keyRotation', parseInt(value) || 30)}
              type="number"
              placeholder="30"
              disabled={disabled}
              helpText="Days between encryption key rotations (recommended: 30 days)"
            />
          </Grid>
          
          {config.encryption.currentKeyId && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Current Key ID"
                value={config.encryption.currentKeyId}
                onChange={() => {}} // Read-only
                type="text"
                disabled={true}
                helpText="Current encryption key identifier"
              />
            </Grid>
          )}
          
          {config.encryption.lastRotation && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Last Rotation"
                value={new Date(config.encryption.lastRotation).toLocaleDateString()}
                onChange={() => {}} // Read-only
                type="text"
                disabled={true}
                helpText="Date of last key rotation"
              />
            </Grid>
          )}
          
          {config.encryption.nextRotation && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Next Rotation"
                value={new Date(config.encryption.nextRotation).toLocaleDateString()}
                onChange={() => {}} // Read-only
                type="text"
                disabled={true}
                helpText="Scheduled date for next key rotation"
              />
            </Grid>
          )}
        </Grid>
      </ConfigSection>
    </Box>
  )
}

export default SecuritySettings 